import { useState, useEffect } from "react";
import { List, X, Phone } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

const NAV = [
  { label: "Services", id: "services" },
  { label: "How it works", id: "how" },
  { label: "Insurance", id: "insurance" },
  { label: "Coverage", id: "coverage" },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 border-b transition-colors duration-300 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-white/10" : "bg-black/30 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 h-16 flex items-center justify-between">
        <button
          data-testid="logo-btn"
          onClick={() => scrollTo("hero")}
          className="flex items-center gap-2.5 group"
        >
          <span className="grid place-items-center h-8 w-8 bg-rescue-primary text-white font-display text-xl leading-none">
            R
          </span>
          <span className="font-display text-2xl uppercase tracking-tight leading-none">
            Rapid<span className="text-rescue-primary">Rescue</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-${n.id}`}
              onClick={() => scrollTo(n.id)}
              className="text-sm font-medium text-white/70 hover:text-white transition-colors"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="tel:+919892648175"
            data-testid="header-call"
            className="flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white"
          >
            <Phone weight="fill" className="text-rescue-primary" size={18} />
            +91 98926 48175
          </a>
          <button
            data-testid="header-request-btn"
            onClick={() => scrollTo("request")}
            className="bg-rescue-primary hover:brightness-110 active:scale-95 transition-all text-white text-sm font-semibold px-5 py-2.5 rounded-sm uppercase tracking-wide"
          >
            Request Help
          </button>
        </div>

        <button
          data-testid="mobile-menu-btn"
          className="md:hidden text-white"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={26} /> : <List size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/90 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-5 py-5 flex flex-col gap-4">
              {NAV.map((n) => (
                <button
                  key={n.id}
                  data-testid={`mobile-nav-${n.id}`}
                  onClick={() => {
                    scrollTo(n.id);
                    setOpen(false);
                  }}
                  className="text-left text-lg font-medium text-white/80"
                >
                  {n.label}
                </button>
              ))}
              <button
                data-testid="mobile-request-btn"
                onClick={() => {
                  scrollTo("request");
                  setOpen(false);
                }}
                className="bg-rescue-primary text-white font-semibold px-5 py-3 rounded-sm uppercase mt-1"
              >
                Request Help
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export { scrollTo };
